import { useEffect, useRef, useState } from 'react';
// Type-only import + dynamic import for runtime — see flyToLocation. Keeps
// Cesium out of the static graph so the 2D bundle (and offline PWA
// precache) stays Cesium-free.
import type * as Cesium from 'cesium';
import type L from 'leaflet';
import { useT } from '../i18n';
import { isLowDataMode, setLowDataMode } from '../viewer/lowDataMode';
import { snapshotCurrentViewUrl } from '../viewer/snapshotUrl';
import { useNarrowViewport } from './useNarrowViewport';

type WindowExt = Window & {
  cesiumViewer?: Cesium.Viewer;
  leafletMap?: L.Map & {
    setBearing?: (deg: number) => void;
    getBearing?: () => number;
  };
};

// --- Preset views captured with the "Share this view" button ---
type LocationDef = {
  id: string;
  emoji: string;
  label: string;
  // 3D camera (Cesium).
  lon: number;
  lat: number;
  height: number;
  heading: number;
  pitch: number;
  // Optional 2D override (Leaflet) — falls back to lon/lat with a default
  // zoom / bearing when omitted. To be filled in by the editor.
  view2d?: { lon?: number; lat?: number; zoom: number; bearing?: number };
};

const LOCATIONS: LocationDef[] = [
  {
    id: 'manglillo',
    emoji: '🏝️',
    label: 'El Manglillo',
    lon: -63.990208,
    lat: 10.888882,
    height: 663.5,
    heading: 359.72,
    pitch: -52.91,
    view2d: { lon: -63.989595, lat: 10.895318, zoom: 16, bearing: 7.11 },
  },
  {
    id: 'crypto-city',
    emoji: '🌆',
    label: 'Crypto City',
    lon: -64.001461,
    lat: 10.863903,
    height: 2739.1,
    heading: 15.83,
    pitch: -46.03,
    view2d: { lon: -63.996018, lat: 10.889412, zoom: 15, bearing: 338.70 },
  },
  {
    id: 'el-yaque',
    emoji: '🏄',
    label: 'El Yaque',
    lon: -63.937160,
    lat: 10.897473,
    height: 2451.8,
    heading: 267.64,
    pitch: -31.78,
    view2d: { lon: -63.978579, lat: 10.897620, zoom: 14, bearing: 87.80 },
  },
  {
    id: 'pmv',
    emoji: '✈️',
    label: 'Aeropuerto',
    lon: -63.959912,
    lat: 10.936303,
    height: 1528.0,
    heading: 216.8,
    pitch: -22.4,
    view2d: { lon: -63.974131, lat: 10.906771, zoom: 14, bearing: 143.48 },
  },
];

async function flyToLocation(loc: LocationDef) {
  if (isLowDataMode()) {
    const map = (window as WindowExt).leafletMap;
    if (!map) return;
    const lat = loc.view2d?.lat ?? loc.lat;
    const lon = loc.view2d?.lon ?? loc.lon;
    const zoom = loc.view2d?.zoom ?? 14;
    map.flyTo([lat, lon], zoom, { duration: 1.5 });
    if (typeof map.setBearing === 'function') {
      map.setBearing(loc.view2d?.bearing ?? 0);
    }
    return;
  }
  const v = (window as WindowExt).cesiumViewer;
  if (!v) return;
  // Cesium is only needed for the 3D fly-to math — dynamic-import keeps it
  // out of the 2D bundle.
  const Cesium = await import('cesium');
  v.camera.flyTo({
    destination: Cesium.Cartesian3.fromDegrees(loc.lon, loc.lat, loc.height),
    orientation: {
      heading: Cesium.Math.toRadians(loc.heading),
      pitch: Cesium.Math.toRadians(loc.pitch),
      roll: 0,
    },
    duration: 2.5,
  });
}

// snapshotCurrentViewUrl lives in src/viewer/snapshotUrl.ts so the floating
// PhoneUrlButton and ShareButton can reuse the exact same logic if needed.

export function PositionHUD() {
  const { t } = useT();
  // Default: expanded on desktop, collapsed on mobile so it doesn't cover
  // the map. Viewport width is a more reliable mobile signal than
  // `ontouchstart` (touchscreen laptops and Chrome's mobile-emulation
  // preset both fool a touch-only check).
  const narrow = useNarrowViewport();
  const [expanded, setExpanded] = useState(!narrow);
  const [copied, setCopied] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Click outside the panel → collapse it.
  useEffect(() => {
    if (!expanded) return;
    const onPointerDown = (e: PointerEvent) => {
      const el = containerRef.current;
      if (!el) return;
      if (e.target instanceof Node && el.contains(e.target)) return;
      setExpanded(false);
    };
    // Use 'pointerdown' so it fires before the canvas click handler picks lots.
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [expanded]);

  const onCopy = async () => {
    const text = snapshotCurrentViewUrl();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt('Share URL (copy):', text);
    }
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        top: 52,            // 36 px sticky warning banner + 16 px gap
        left: 16,
        background: 'rgba(0,0,0,0.62)',
        color: 'white',
        fontSize: 12,
        lineHeight: 1.5,
        borderRadius: 8,
        pointerEvents: 'auto',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        userSelect: 'none',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        maxWidth: expanded ? 260 : 'auto',
        maxHeight: expanded ? 'calc(100vh - 68px)' : 'auto',
        overflowY: expanded ? 'auto' : 'visible',
      }}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          ...btn,
          width: '100%',
          padding: '8px 10px',
          background: 'rgba(255,255,255,0.08)',
          borderRadius: expanded ? '8px 8px 0 0' : 8,
          borderBottom: expanded
            ? '1px solid rgba(255,255,255,0.18)'
            : '1px solid transparent',
          textAlign: 'left',
          fontWeight: 600,
        }}
        aria-expanded={expanded}
      >
        <span style={{ marginRight: 8 }}>{expanded ? '✕' : '☰'}</span>
        {expanded ? t('menu_hide') : t('menu_show')}
      </button>

      {expanded && (
        <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {/* Preset locations */}
          <SectionLabel>{t('locations')}</SectionLabel>
          {LOCATIONS.map((loc) => (
            <button
              key={loc.id}
              onClick={() => {
                flyToLocation(loc);
                setExpanded(false); // collapse on selection
              }}
              style={{ ...btn, justifyContent: 'flex-start' }}
            >
              <span style={{ width: 22, display: 'inline-block' }}>{loc.emoji}</span>
              {loc.id === 'pmv' ? t('preset_airport') : loc.label}
            </button>
          ))}

          {/* Share view */}
          <Divider />
          <button
            onClick={onCopy}
            title="Copy a shareable URL that opens this exact view"
            style={btn}
          >
            {copied ? `✓ ${t('share_copied')}` : `🔗 ${t('share_view')}`}
          </button>

          {/* Language toggle lives in the standalone LanguageToggle component
              now — see src/ui/LanguageToggle.tsx (rendered from App.tsx). */}

          {/* Mode switcher — different action depending on which viewer
              is currently active. The footer no longer hosts this toggle. */}
          <Divider />
          {isLowDataMode() ? (
            <button
              onClick={() => {
                setLowDataMode(false);
                // Drop the 2D camera params (and any `?mode=2d`) so the
                // inline boot script doesn't bounce us straight back.
                const clean = new URL(window.location.href);
                const lot = clean.searchParams.get('lot');
                clean.search = '';
                if (lot) clean.searchParams.set('lot', lot);
                window.location.replace(clean.toString());
              }}
              title={t('switch_to_3d')}
              style={btn}
            >
              🌐 {t('mode_3d')}
            </button>
          ) : (
            <button
              onClick={() => {
                setLowDataMode(true);
                // Drop camera/zoom params on mode switch — only keep `lot`.
                const clean = new URL(window.location.href);
                const lot = clean.searchParams.get('lot');
                clean.search = '';
                if (lot) clean.searchParams.set('lot', lot);
                window.location.replace(clean.toString());
              }}
              title="Switch to the lightweight 2D map (lower data usage)"
              style={btn}
            >
              ⚡ {t('low_data_mode')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 10,
        opacity: 0.55,
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        margin: '2px 0',
      }}
    >
      {children}
    </div>
  );
}

function Divider() {
  return (
    <div
      style={{
        height: 1,
        background: 'rgba(255,255,255,0.18)',
        margin: '6px 0',
      }}
    />
  );
}

const btn: React.CSSProperties = {
  padding: '6px 10px',
  borderRadius: 5,
  border: '1px solid rgba(255,255,255,0.2)',
  background: 'rgba(255,255,255,0.06)',
  color: 'white',
  fontFamily: 'inherit',
  fontSize: 12,
  cursor: 'pointer',
  textAlign: 'left',
  whiteSpace: 'nowrap',
};
